import { FC, useCallback, useEffect, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { TSharingEntityType, TSharedEnumOptions, TSharedMasterData } from "@/api/services/main/sharing/dtos/sharing.dto";
import { verifySharingApi } from "@/api/services/main/sharing";
import { SharedEnumProvider } from "@/features/sharing/context/shared-enum-context";
import { SharedCaseDetail } from "@/features/sharing/components/SharedCaseDetail";
import { SharedTalentDetail } from "@/features/sharing/components/SharedTalentDetail";
import { SharedJobDetail } from "@/features/sharing/components/SharedJobDetail";
import { TSharedCaseData } from "@/features/sharing/types/shared-case";
import { getCredentialsKey } from "@/features/sharing/utils/session-credentials";
import { CircularProgress, Stack } from "@mui/material";

type TResolvedData = {
  entityType: TSharingEntityType;
  entityData: Record<string, unknown>;
  shareType?: number | null;
  enumOptions?: TSharedEnumOptions;
  masterData?: TSharedMasterData;
};

/** Renders entity-specific detail component based on entityType from verify response */
export const SharedDetailPage: FC = () => {
  const { hash } = useParams<{ hash: string }>();
  const location = useLocation();
  const state = location.state as TResolvedData | null;

  const [resolvedData, setResolvedData] = useState<TResolvedData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  /** Re-verify using stored credentials for fresh data */
  const reverifyFromStorage = useCallback(async () => {
    if (!hash) return null;
    try {
      const stored = sessionStorage.getItem(getCredentialsKey(hash));
      if (!stored) return null;
      const { shareUrl, sharePassword } = JSON.parse(stored) as { shareUrl: string; sharePassword: string };
      const result = await verifySharingApi({ shareUrl, sharePassword });
      return {
        entityType: result.data.entityType,
        entityData: result.data.data,
        shareType: result.data.shareType,
        enumOptions: result.data.enumOptions,
        masterData: result.data.masterData,
      } as TResolvedData;
    } catch {
      // Credentials invalid or expired — clear and redirect to password page
      sessionStorage.removeItem(getCredentialsKey(hash));
      return null;
    }
  }, [hash]);

  useEffect(() => {
    const hasStoredCredentials = !!hash && !!sessionStorage.getItem(getCredentialsKey(hash));

    // If credentials exist in sessionStorage, always re-verify for fresh data (handles reload)
    if (hasStoredCredentials) {
      let cancelled = false;
      setIsLoading(true);
      reverifyFromStorage().then((data) => {
        if (cancelled) return;
        if (data) {
          setResolvedData(data);
        } else {
          setShouldRedirect(true);
        }
        setIsLoading(false);
      });
      return () => {
        cancelled = true;
      };
    }

    // First navigation from password page — use location state directly (already fresh)
    if (state?.entityType && state?.entityData) {
      setResolvedData({ entityType: state.entityType, entityData: state.entityData, shareType: state.shareType, enumOptions: state.enumOptions, masterData: state.masterData });
      return;
    }

    // No credentials and no location state — redirect to password form
    setShouldRedirect(true);
  }, [hash, state, reverifyFromStorage]);

  if (shouldRedirect) {
    return <Navigate to={`/shared/${hash}`} replace />;
  }

  if (isLoading || !resolvedData) {
    return (
      <Stack alignItems="center" justifyContent="center" minHeight="50vh">
        <CircularProgress />
      </Stack>
    );
  }

  const renderEntity = () => {
    switch (resolvedData.entityType) {
      case "case":
        return <SharedCaseDetail data={resolvedData.entityData as unknown as TSharedCaseData} />;
      case "talent":
        return <SharedTalentDetail hash={hash!} data={resolvedData.entityData} shareType={resolvedData.shareType} />;
      case "job": {
        const storedPassword = sessionStorage.getItem(`shared_password_${hash}`) ?? undefined;
        return <SharedJobDetail hash={hash!} data={resolvedData.entityData} sharePassword={storedPassword} />;
      }
      default:
        return <Navigate to={`/shared/${hash}`} replace />;
    }
  };

  return (
    <SharedEnumProvider enumOptions={resolvedData.enumOptions} masterData={resolvedData.masterData}>
      {renderEntity()}
    </SharedEnumProvider>
  );
};
